import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";

const NewsFeed = () => {
  const { toast } = useToast();
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const { data: news, isLoading, isError } = useQuery({
    queryKey: ["news"],
    queryFn: async () => {
      console.log("Fetching news data...");
      const { data, error } = await supabase
        .from("news")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching news:", error);
        throw error;
      }
      
      console.log("News data fetched successfully:", data);
      return data;
    },
    meta: {
      onError: (error: Error) => {
        console.error("Query error:", error);
        toast({
          title: "Error loading news",
          description: "Please try again later",
          variant: "destructive",
        });
      },
    },
  });

  useEffect(() => {
    if (!news?.length) return;

    const interval = setInterval(() => {
      setCurrentIndex((current) => (current + 1) % news.length);
    }, 5000); // Change announcement every 5 seconds

    return () => clearInterval(interval);
  }, [news?.length]);

  if (isError) {
    return (
      <div className="bg-destructive/10 border-b">
        <div className="h-12 flex items-center justify-center gap-2 text-destructive">
          <AlertCircle size={16} />
          <span>Unable to load news feed</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-primary/5 border-b">
        <div className="h-12 flex items-center px-4">
          <div className="w-full flex gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-2">
                <div className="h-4 w-20 bg-primary/10 rounded" />
                <div className="h-4 w-40 bg-primary/10 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!news?.length) {
    return (
      <div className="bg-muted border-b">
        <div className="h-12 flex items-center justify-center text-muted-foreground">
          No announcements at this time
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary/5 border-b">
      <div className="h-12 relative overflow-hidden">
        {news.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 flex items-center justify-center px-4 transition-opacity duration-500 ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="flex items-center gap-2 animate-fadeIn">
              <span className="font-semibold">{item.title}:</span>
              <span className="text-muted-foreground">{item.content}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsFeed;